import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { forkJoin } from 'rxjs';
import { Campaign } from '../../models/campaign';
import { Roster } from '../../models/roster';
import { Invoice } from '../../models/invoice';
import { CampaignService } from '../../services/campaign.service';
import { RosterService } from '../../services/roster.service';
import { InvoiceService } from '../../services/invoice.service';
import { LoginService } from '../../services/login.service';
import { UserRole, User } from '../../models/user';
import { UserService } from '../../services/user.service';
import {
  Chart, ChartData, ChartOptions,
  CategoryScale, LinearScale, LineController,
  PointElement, LineElement, Tooltip, Legend
} from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';

export interface SourceReportRow {
  campaignId: string;
  campaignName: string;
  campaignCreatedDate: string;
  rosterName: string;
  campaignRate: number;
  campaignCommission: number;
  invoiceNumber: string;
  invoiceDueDate: string;
  /** Displayed invoice amount: actualPayAmount when paid, otherwise sum of lineItems */
  invoiceAmount: number;
  actualPayAmount: number | undefined;
  creatorPayAmount: number | undefined;
  /** Total of the invoice's expense line items (VtPaidExpense + CreatorPaidExpense).
   *  Excluded from the projection base. */
  expenseAmount: number | undefined;
  /** Portion of the expense VT keeps; subtracted from commission on paid invoices. */
  vtKeptExpense: number | undefined;
  /** Total of CreatorPaidCommission lines — counts as commission at full value (no rate). */
  commissionAmount: number | undefined;
  /** actualPayAmount - creatorPayAmount - vtKeptExpense (only for fully-paid invoices) */
  commissionEarned: number;
  paidDate?: string;
  isPaid: boolean;
  isNoInvoice: boolean;
  /** Fake row representing the uninvoiced remainder of a partially invoiced campaign */
  isProjected: boolean;
  /** Matches the rowKey stored on the chart data point so click-filtering is exact */
  rowKey: string;
}

export interface BonusRow {
  date: string;
  amount: number;
  isProjected: boolean;
  rowKeys: string[];
  periodCommission: number;
  rosterName?: string;
}

interface CommEvent { date: string; amount: number; isProjected: boolean; rowKey: string; }

@Component({
  selector: 'app-source-report',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, BaseChartDirective],
  templateUrl: './source-report.component.html',
  styleUrl: './source-report.component.css'
})
export class SourceReportComponent implements OnInit {

  campaigns: Campaign[] = [];
  rosters: Roster[] = [];
  invoices: Invoice[] = [];

  /** Lookup maps populated once data is loaded */
  rosterById: Record<string, Roster> = {};
  invoicesByCampaignId: Record<string, Invoice[]> = {};

  agentUsers: User[] = [];

  /** Distinct source values drawn from both campaigns and rosters */
  availableSources: string[] = [];
  selectedSource: string = '';

  bonusAmount: number = 0;
  bonusRows: BonusRow[] = [];

  get isAdmin(): boolean { return this.loginService.user.role === UserRole.Admin; }
  get currentUserFullName(): string { return `${this.loginService.user.firstName} ${this.loginService.user.lastName}`; }

  get selectedAgentUser(): User | undefined {
    return this.agentUsers.find(u => u.id === this.selectedSource);
  }

  get selectedAgentSalary(): number | undefined {
    return this.selectedAgentUser?.salary;
  }

  get showBonusData(): boolean {
    const u = this.selectedAgentUser;
    return !!u && (u.role === UserRole.Agent || u.role === UserRole.Casting || u.role === UserRole.Scouting);
  }

  get isScouting(): boolean { return this.selectedAgentUser?.role === UserRole.Scouting; }

  resolveUserId(id: string): string { return this.userService.resolveUserId(id); }

  getRoleLabel(role: UserRole): string {
    switch (role) {
      case UserRole.Agent:    return 'Agent';
      case UserRole.Scouting: return 'Scouting';
      case UserRole.Casting:  return 'Casting';
      default:                return UserRole[role] ?? 'Unknown';
    }
  }

  /** Chart */
  chartData: ChartData<'line'> | null = null;
  chartOptions: ChartOptions<'line'> = {};
  /** Per-chart plugins (used to draw Scouting threshold lines directly on canvas) */
  chartPlugins: any[] = [];

  /** Rows rendered in the report table */
  rows: SourceReportRow[] = [];

  /** Row keys selected by clicking a chart data point or bonus row; null means no filter.
   *  Each key is `campaignId|invoiceNumber`, `campaignId|no-invoice`, or `campaignId|proj|YYYY-MM-DD`. */
  chartPointFilter: string[] | null = null;
  chartFilterDate: string | null = null;

  /** Index of the currently active bonus row filter (-1 = none) */
  activeBonusRowIndex: number = -1;

  private readonly externalTooltip = (context: any) => {
    const { chart, tooltip } = context;
    const canvas = chart.canvas as HTMLCanvasElement;
    const parent = canvas.parentElement as HTMLElement;

    let el = parent.querySelector<HTMLDivElement>('#vtg-sr-tooltip');
    if (!el) {
      el = document.createElement('div');
      el.id = 'vtg-sr-tooltip';
      el.style.cssText = 'position:absolute;pointer-events:none;z-index:20;transition:opacity 0.1s;';
      parent.appendChild(el);
    }

    if (tooltip.opacity === 0) { el.style.opacity = '0'; return; }
    if (!tooltip.dataPoints?.length) return;

    const dp  = tooltip.dataPoints[0];
    const raw = dp.raw as any;
    const campaigns: { id: string; label?: string; amount: number }[] = raw?.campaigns ?? [];
    const delta: number  = raw?.delta ?? 0;
    const total: number  = dp.parsed.y;
    const ts:    number  = dp.parsed.x;

    const fmt  = (n: number) => '$' + n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    const date = new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

    const tdId  = `padding-right:14px;color:#bbb;font-family:'Courier New',monospace;font-size:12px;`;
    const tdAmt = `text-align:right;color:#fff;font-family:'Courier New',monospace;font-size:12px;white-space:nowrap;`;

    let rows = campaigns.map(c =>
      `<tr><td style="${tdId}">${c.label ?? c.id.substring(0, 8)}</td><td style="${tdAmt}">${fmt(c.amount)}</td></tr>`
    ).join('');

    if (delta > 0) {
      rows += `<tr><td colspan="2" style="padding:3px 0;"><div style="border-top:1px solid #555;"></div></td></tr>`;
      rows += `<tr><td></td><td style="${tdAmt}font-weight:bold;">+${fmt(delta)}</td></tr>`;
    }

    el.innerHTML = `
      <div style="background:rgba(0,0,0,0.88);color:#fff;border-radius:6px;padding:10px 14px;font-size:13px;border:1px solid #444;min-width:200px;">
        <div style="font-weight:bold;margin-bottom:${rows ? 8 : 0}px;white-space:nowrap;">${date}&nbsp;&nbsp;&nbsp;${fmt(total)}</div>
        ${rows ? `<table style="border-collapse:collapse;width:100%;">${rows}</table>` : ''}
      </div>`;

    el.style.opacity = '1';

    const cw = canvas.offsetWidth;
    const x  = tooltip.caretX;
    const y  = tooltip.caretY;
    if (x > cw / 2) {
      el.style.left  = '';
      el.style.right = `${cw - x + 12}px`;
    } else {
      el.style.right = '';
      el.style.left  = `${x + 12}px`;
    }
    el.style.top = `${Math.max(0, y - 10)}px`;
  };

  get visibleRows(): SourceReportRow[] {
    if (!this.chartPointFilter) return this.rows;
    const keys = new Set(this.chartPointFilter);
    return this.rows.filter(r => keys.has(r.rowKey));
  }

  get totalCommission(): number {
    if (this.isScouting) {
      return this.visibleRows.reduce((s, r) => s + r.campaignRate, 0);
    }
    const activeBonusRow = this.activeBonusRowIndex >= 0 ? this.bonusRows[this.activeBonusRowIndex] : null;
    if (activeBonusRow?.periodCommission) {
      return activeBonusRow.periodCommission;
    }
    return this.visibleRows.reduce((sum, row) => {
      let commission: number;
      if (row.isPaid) {
        commission = row.commissionEarned;
      } else if (!row.isNoInvoice) {
        const commissionAmt = row.commissionAmount ?? 0;
        commission = (row.invoiceAmount - (row.expenseAmount ?? 0) - commissionAmt) * row.campaignCommission / 100 + commissionAmt;
      } else {
        commission = row.campaignRate * row.campaignCommission / 100;
      }
      return sum + commission;
    }, 0);
  }

  onChartClick(event: { event?: any; active?: any[] }): void {
    const active = event.active;
    if (!active?.length) return;
    const { datasetIndex, index } = active[0];
    const ds = this.chartData?.datasets?.[datasetIndex];
    if (!ds) return;
    const rowKeys: string[] = (ds.data[index] as any)?.rowKeys ?? [];
    if (!rowKeys.length) return;
    this.chartPointFilter = rowKeys;
    const ts: number | undefined = (ds.data[index] as any)?.x;
    this.chartFilterDate = ts != null
      ? new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      : null;
  }

  clearChartFilter(): void {
    this.chartPointFilter = null;
    this.chartFilterDate = null;
    this.activeBonusRowIndex = -1;
  }

  onBonusRowClick(row: BonusRow, index: number): void {
    if (this.activeBonusRowIndex === index) {
      this.chartPointFilter = null;
      this.activeBonusRowIndex = -1;
    } else {
      this.chartPointFilter = row.rowKeys;
      this.activeBonusRowIndex = index;
    }
  }

  /** Sum of (actualPayAmount - creatorPayAmount) for fully-paid invoices */
  currentCommission: number = 0;

  /**
   * Projected commission from:
   * 1. Unpaid invoices: lineItemTotal * (commission / 100)
   * 2. Campaigns with no invoices: campaign.rate * (commission / 100)
   */
  futureCommission: number = 0;

  loading: boolean = true;

  constructor(
    private campaignService: CampaignService,
    private rosterService: RosterService,
    private invoiceService: InvoiceService,
    private loginService: LoginService,
    private userService: UserService
  ) {}

  ngOnInit(): void {
    Chart.register(CategoryScale, LinearScale, LineController, PointElement, LineElement, Tooltip, Legend);

    forkJoin({
      campaigns: this.campaignService.getCampaigns(),
      rosters: this.rosterService.getRosters(),
      invoices: this.invoiceService.getInvoices(),
      users: this.userService.getUsers()
    }).subscribe({
      next: ({ campaigns, rosters, invoices, users }) => {
        this.agentUsers = (users || []).filter(u => u.role === UserRole.Agent || u.role === UserRole.Scouting || u.role === UserRole.Casting);
        this.campaigns = (campaigns || []).filter(c => !(c as any).deleted);
        this.rosters = rosters || [];
        this.invoices = invoices || [];
        this.buildLookups();
        this.buildSourceList();
        if (!this.isAdmin) {
          this.selectedSource = this.loginService.user.id;
          this.buildReport();
        }
        this.loading = false;
      },
      error: err => {
        console.error('Error loading source report data:', err);
        this.loading = false;
      }
    });
  }

  // ─── Private helpers ─────────────────────────────────────────────────────

  /** Per-invoice totals derived from line item types.
   *  - expenseTotal: non-commissionable expense lines (VtPaidExpense + CreatorPaidExpense)
   *    → excluded from the projection base
   *  - vtKeptExpense: only the VtPaidExpense lines → subtracted from paid commission
   *  - commissionTotal: CreatorPaidCommission lines, whose amount IS commission (the
   *    creator paid VT directly), so the full amount counts as commission (rate not applied) */
  private lineItemTotals(invoice: Invoice): { expenseTotal: number; vtKeptExpense: number; commissionTotal: number } {
    let expenseTotal = 0;
    let vtKeptExpense = 0;
    let commissionTotal = 0;
    for (const item of invoice.lineItems || []) {
      const amount = (item.unitPrice || 0);
      switch (item.lineItemType) {
        case 'VtPaidExpense':         expenseTotal += amount; vtKeptExpense += amount; break;
        case 'CreatorPaidExpense':    expenseTotal += amount;                          break;
        case 'CreatorPaidCommission': commissionTotal += amount;                       break;
        // BasePayment / undefined: commissionable, no special total
      }
    }
    return { expenseTotal, vtKeptExpense, commissionTotal };
  }

  private buildLookups(): void {
    this.rosterById = {};
    this.rosters.forEach(r => (this.rosterById[r.id] = r));

    this.invoicesByCampaignId = {};
    this.invoices.forEach(inv => {
      if (!this.invoicesByCampaignId[inv.campaignId]) {
        this.invoicesByCampaignId[inv.campaignId] = [];
      }
      this.invoicesByCampaignId[inv.campaignId].push(inv);
    });
  }

  private readonly ignoredSources = new Set(['Inbound', 'Not Set']);

  private buildSourceList(): void {
    const sources = new Set<string>();
    this.campaigns.forEach(c => {
      if (c.source && !this.ignoredSources.has(c.source.trim())) sources.add(c.source.trim());
    });
    this.rosters.forEach(r => {
      if (r.source && !this.ignoredSources.has(r.source.trim())) sources.add(r.source.trim());
    });
    this.availableSources = Array.from(sources).sort();
  }

  // ─── Public event handlers ────────────────────────────────────────────────

  onSourceChange(): void {
    if (!this.selectedSource) {
      this.rows = [];
      this.currentCommission = 0;
      this.futureCommission = 0;
      this.bonusAmount = 0;
      this.bonusRows = [];
      this.chartData = null;
      return;
    }
    this.buildReport();
  }

  // ─── Report builder ───────────────────────────────────────────────────────

  private buildReport(): void {
    const role = this.selectedAgentUser?.role;
    let filteredCampaigns: Campaign[];

    if (role === UserRole.Agent) {
      filteredCampaigns = this.campaigns.filter(c => c.agentId === this.selectedSource);
    } else if (role === UserRole.Casting) {
      filteredCampaigns = this.campaigns.filter(c => c.source?.trim() === this.selectedSource);
    } else if (role === UserRole.Scouting) {
      filteredCampaigns = this.campaigns.filter(c => {
        const roster = c.creatorId ? this.rosterById[c.creatorId] : null;
        return roster?.source?.trim() === this.selectedSource;
      });
    } else {
      filteredCampaigns = [];
    }

    const newRows: SourceReportRow[] = [];
    let currentCommission = 0;
    let futureCommission = 0;

    // ── Scouting: one row per campaign (gross bookings only, no invoice data) ─
    if (role === UserRole.Scouting) {
      for (const campaign of filteredCampaigns) {
        const roster = campaign.creatorId ? this.rosterById[campaign.creatorId] : null;
        const rosterName = roster?.name || campaign.creatorName || '—';
        currentCommission += campaign.rate || 0;
        newRows.push({
          campaignId: campaign.id,
          campaignName: campaign.name || '—',
          campaignCreatedDate: campaign.createdDate || '',
          rosterName,
          campaignRate: campaign.rate || 0,
          campaignCommission: 0,
          invoiceNumber: '—',
          invoiceDueDate: '',
          invoiceAmount: campaign.rate || 0,
          actualPayAmount: undefined,
          creatorPayAmount: undefined,
          expenseAmount: undefined,
          vtKeptExpense: undefined,
          commissionAmount: undefined,
          commissionEarned: 0,
          isPaid: false,
          isNoInvoice: true,
          isProjected: false,
          rowKey: campaign.id + '|scouting'
        });
      }
      this.rows = newRows.sort((a, b) => b.campaignCreatedDate.localeCompare(a.campaignCreatedDate));
      this.currentCommission = currentCommission;
      this.futureCommission = 0;
      this.chartPointFilter = null;
      this.chartFilterDate = null;
      this.activeBonusRowIndex = -1;
      this.buildChart(filteredCampaigns);
      this.buildBonusData();
      return;
    }

    for (const campaign of filteredCampaigns) {
      const roster = campaign.creatorId ? this.rosterById[campaign.creatorId] : null;
      const rosterName = roster?.name || campaign.creatorName || '—';
      const campaignInvoices = this.invoicesByCampaignId[campaign.id] || [];
      const commissionRate = (campaign.commission || 0) / 100;

      if (campaignInvoices.length === 0) {
        // ── No invoice yet: contribute to future commission ──────────────────
        futureCommission += (campaign.rate || 0) * commissionRate;

        newRows.push({
          campaignId: campaign.id,
          campaignName: campaign.name || '—',
          campaignCreatedDate: campaign.createdDate || '',
          rosterName,
          campaignRate: campaign.rate || 0,
          campaignCommission: campaign.commission || 0,
          invoiceNumber: 'No Invoice',
          invoiceDueDate: '',
          invoiceAmount: campaign.rate || 0,
          actualPayAmount: undefined,
          creatorPayAmount: undefined,
          expenseAmount: undefined,
          vtKeptExpense: undefined,
          commissionAmount: undefined,
          commissionEarned: 0,
          isPaid: false,
          isNoInvoice: true,
          isProjected: false,
          rowKey: campaign.id + '|no-invoice'
        });
      } else {
        let totalInvoicedLineItems = 0;

        for (const invoice of campaignInvoices) {
          const lineItemTotal = (invoice.lineItems || []).reduce(
            (sum, item) => sum + (item.unitPrice || 0),
            0
          );
          // Totals derived from the line item types (drive projection + paid math).
          const { expenseTotal, vtKeptExpense, commissionTotal } = this.lineItemTotals(invoice);
          totalInvoicedLineItems += lineItemTotal;

          // "Paid" means a paidDate is recorded OR actualPayAmount is set.
          const isPaid = !!invoice.paidDate || (invoice.actualPayAmount != null && invoice.actualPayAmount > 0);
          // Earned = brand pay − creator pay − expense VT keeps. A pass-through expense is
          // already inside creatorPayAmount and cancels out (no double-count); only the
          // portion VT retains is non-commissionable money still sitting in actualPay.
          const earned = (invoice.actualPayAmount ?? lineItemTotal) - (invoice.creatorPayAmount ?? 0) - vtKeptExpense;

          if (isPaid) {
            // ── Paid invoice: contribute to current commission ───────────────
            currentCommission += earned;

            newRows.push({
              campaignId: campaign.id,
              campaignName: campaign.name || '—',
              campaignCreatedDate: campaign.createdDate || '',
              rosterName,
              campaignRate: campaign.rate || 0,
              campaignCommission: campaign.commission || 0,
              invoiceNumber: invoice.invoiceNumber || '—',
              invoiceDueDate: invoice.dueDate || '',
              invoiceAmount: invoice.actualPayAmount!,
              actualPayAmount: invoice.actualPayAmount,
              creatorPayAmount: invoice.creatorPayAmount,
              expenseAmount: expenseTotal,
              vtKeptExpense: vtKeptExpense,
              commissionAmount: commissionTotal,
              commissionEarned: earned,
              paidDate: invoice.paidDate || invoice.creatorPaidDate,
              isPaid: true,
              isNoInvoice: false,
              isProjected: false,
              rowKey: campaign.id + '|' + (invoice.invoiceNumber || '—')
            });
          } else {
            // ── Unpaid invoice: contribute to future commission ──────────────
            // Base lines accrue at the commission rate; CreatorPaidCommission lines are
            // already commission, so their full amount is added directly.
            futureCommission += (lineItemTotal - expenseTotal - commissionTotal) * commissionRate + commissionTotal;

            newRows.push({
              campaignId: campaign.id,
              campaignName: campaign.name || '—',
              campaignCreatedDate: campaign.createdDate || '',
              rosterName,
              campaignRate: campaign.rate || 0,
              campaignCommission: campaign.commission || 0,
              invoiceNumber: invoice.invoiceNumber || '—',
              invoiceDueDate: invoice.dueDate || '',
              invoiceAmount: lineItemTotal,
              actualPayAmount: invoice.actualPayAmount,
              creatorPayAmount: invoice.creatorPayAmount,
              expenseAmount: expenseTotal,
              vtKeptExpense: vtKeptExpense,
              commissionAmount: commissionTotal,
              commissionEarned: 0,
              isPaid: false,
              isNoInvoice: false,
              isProjected: false,
              rowKey: campaign.id + '|' + (invoice.invoiceNumber || '—')
            });
          }
        }

        // ── Uninvoiced remainder: fake projected rows for table + chart ──────
        // Skip for direct-pay campaigns: the brand paid the creator directly and the
        // creator forwarded VT's commission, so there is no uninvoiced remainder to
        // project. Detected either by a paid invoice with creatorPayAmount === 0 or by
        // any CreatorPaidCommission line (the explicit marker for this scenario).
        const isDirectPay = campaignInvoices.some(inv =>
          ((!!inv.paidDate || (inv.actualPayAmount != null && inv.actualPayAmount > 0)) &&
            inv.creatorPayAmount === 0) ||
          (inv.lineItems || []).some(li => li.lineItemType === 'CreatorPaidCommission')
        );
        const remainder = (campaign.rate || 0) - totalInvoicedLineItems;
        if (remainder > 100 && !isDirectPay) {
          futureCommission += remainder * commissionRate;
          const [d1, d2] = this.twoMonthSpreadDates(campaign.createdDate);
          const halfAmount = remainder / 2;
          for (const projDate of [d1, d2]) {
            newRows.push({
              campaignId: campaign.id,
              campaignName: campaign.name || '—',
              campaignCreatedDate: campaign.createdDate || '',
              rosterName,
              campaignRate: campaign.rate || 0,
              campaignCommission: campaign.commission || 0,
              invoiceNumber: 'Projected',
              invoiceDueDate: projDate,
              invoiceAmount: halfAmount,
              actualPayAmount: undefined,
              creatorPayAmount: undefined,
              expenseAmount: undefined,
              vtKeptExpense: undefined,
              commissionAmount: undefined,
              commissionEarned: 0,
              isPaid: false,
              isNoInvoice: false,
              isProjected: true,
              rowKey: campaign.id + '|proj|' + projDate
            });
          }
        }
      }
    }

    this.rows = newRows.sort((a, b) => b.campaignCreatedDate.localeCompare(a.campaignCreatedDate));
    this.currentCommission = currentCommission;
    this.futureCommission = futureCommission;
    this.chartPointFilter = null;
    this.chartFilterDate = null;
    this.activeBonusRowIndex = -1;
    this.buildChart(filteredCampaigns);
    this.buildBonusData();
  }

  // ─── Projected pay date helper ──────────────────────────────────────────────
  // Used for no-invoice campaigns: returns createdDate + 71 days.
  // If that date is in the past, spreads deterministically 1–45 days from today.
  private projectedPayDate(createdDate: string | undefined): string {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const base = createdDate ? new Date(createdDate + 'T00:00:00') : today;
    const naive = new Date(base.getTime() + 71 * 24 * 60 * 60 * 1000);
    if (naive >= today) {
      return naive.toISOString().substring(0, 10);
    }
    const spreadDays = (base.getDate() + base.getMonth() * 3) % 45 + 1;
    return new Date(today.getTime() + spreadDays * 24 * 60 * 60 * 1000).toISOString().substring(0, 10);
  }

  // ─── Two-month spread helper ────────────────────────────────────────────────
  // Used for overdue invoices and remainders: splits the amount across two
  // deterministic dates — the first 1–30 days from today, the second 30 days
  // after that — so the commission appears as a gradual rise rather than a spike.
  private twoMonthSpreadDates(seed: string | undefined): [string, string] {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const s = seed ? new Date(seed + 'T00:00:00') : today;
    const offset = (s.getDate() + s.getMonth() * 3) % 30 + 1;
    const d1 = new Date(today.getTime() + offset * 24 * 60 * 60 * 1000);
    const d2 = new Date(today.getTime() + (offset + 30) * 24 * 60 * 60 * 1000);
    return [d1.toISOString().substring(0, 10), d2.toISOString().substring(0, 10)];
  }

  // ─── Chart builder ─────────────────────────────────────────────────────────

  private buildChart(filteredCampaigns: Campaign[]): void {
    const isScouting = this.selectedAgentUser?.role === UserRole.Scouting;

    // ── Scouting: cumulative gross bookings by campaign.createdDate ───────────
    if (isScouting) {
      this.buildScoutingChart(filteredCampaigns);
      return;
    }

    this.chartPlugins = [];

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayStr = today.toISOString().substring(0, 10);

    interface Pt { date: string; value: number; campaignId: string; rowKey: string; }
    const paidPts: Pt[] = [];
    const projPts: Pt[] = [];

    for (const campaign of filteredCampaigns) {
      const commissionRate = (campaign.commission || 0) / 100;
      const campaignInvoices = this.invoicesByCampaignId[campaign.id] || [];

      if (campaignInvoices.length === 0) {
        // No invoice yet: single projected point using createdDate + 71 days logic.
        projPts.push({ date: this.projectedPayDate(campaign.createdDate), value: (campaign.rate || 0) * commissionRate, campaignId: campaign.id, rowKey: campaign.id + '|no-invoice' });
      } else {
        let totalInvoicedLineItems = 0;
        let anyPaid = false;

        for (const invoice of campaignInvoices) {
          const lineItemTotal = (invoice.lineItems || []).reduce(
            (s, i) => s + (i.unitPrice || 0), 0
          );
          // Totals derived from the line item types (drive projection + paid math).
          const { expenseTotal, vtKeptExpense, commissionTotal } = this.lineItemTotals(invoice);
          totalInvoicedLineItems += lineItemTotal;

          const isPaid = !!invoice.paidDate || (invoice.actualPayAmount != null && invoice.actualPayAmount > 0);

          const invoiceKey = invoice.invoiceNumber || '—';
          if (isPaid) {
            anyPaid = true;
            // Use actual money VT netted (matches Current Commission and bonus events).
            // lineItemTotal × commission% is the contracted rate but can diverge from
            // what was actually paid; the chart should reflect real earnings. Only the
            // expense VT keeps is subtracted — a pass-through expense is already inside
            // the actualPay/creatorPay amounts.
            const earned = (invoice.actualPayAmount ?? lineItemTotal) - (invoice.creatorPayAmount ?? 0) - vtKeptExpense;
            paidPts.push({
              date: invoice.paidDate || invoice.creatorPaidDate || todayStr,
              value: earned,
              campaignId: campaign.id,
              rowKey: campaign.id + '|' + invoiceKey
            });
          } else if (lineItemTotal > 0) {
            // Determine whether this invoice is overdue (due date in the past,
            // or no due date and the createdDate+71 fallback is also past).
            const base = campaign.createdDate ? new Date(campaign.createdDate + 'T00:00:00') : today;
            const naiveDate = invoice.dueDate
              ? new Date(invoice.dueDate + 'T00:00:00')
              : new Date(base.getTime() + 71 * 24 * 60 * 60 * 1000);
            const isOverdue = naiveDate < today;

            // Base lines accrue at the rate; CreatorPaidCommission lines are already
            // commission, so their full amount is added directly.
            const projComm = (lineItemTotal - expenseTotal - commissionTotal) * commissionRate + commissionTotal;

            if (isOverdue) {
              // Overdue: spread commission 50/50 across two dates over the next two months.
              const [d1, d2] = this.twoMonthSpreadDates(invoice.dueDate || campaign.createdDate);
              const half = projComm / 2;
              projPts.push({ date: d1, value: half, campaignId: campaign.id, rowKey: campaign.id + '|' + invoiceKey });
              projPts.push({ date: d2, value: half, campaignId: campaign.id, rowKey: campaign.id + '|' + invoiceKey });
            } else {
              // Future due date: single point on the actual due date.
              projPts.push({ date: invoice.dueDate!, value: projComm, campaignId: campaign.id, rowKey: campaign.id + '|' + invoiceKey });
            }
          }
        }

        // Uninvoiced remainder: always spread 50/50 over the next two months.
        // Skip for direct-pay campaigns (brand pays creator directly; creator pays VT
        // only the commission). Detected by creatorPayAmount === 0 on a paid invoice or
        // any CreatorPaidCommission line (the explicit marker for this scenario).
        const isDirectPay = campaignInvoices.some(inv =>
          ((!!inv.paidDate || (inv.actualPayAmount != null && inv.actualPayAmount > 0)) &&
            inv.creatorPayAmount === 0) ||
          (inv.lineItems || []).some(li => li.lineItemType === 'CreatorPaidCommission')
        );
        const remainder = (campaign.rate || 0) - totalInvoicedLineItems;
        if (remainder > 100 && !isDirectPay) {
          const [d1, d2] = this.twoMonthSpreadDates(campaign.createdDate);
          const half = remainder * commissionRate / 2;
          projPts.push({ date: d1, value: half, campaignId: campaign.id, rowKey: campaign.id + '|proj|' + d1 });
          projPts.push({ date: d2, value: half, campaignId: campaign.id, rowKey: campaign.id + '|proj|' + d2 });
        }
      }
    }

    if (paidPts.length === 0 && projPts.length === 0) {
      this.chartData = null;
      return;
    }

    // ── Aggregate same-date points, tracking per-campaign amounts and row keys ─
    interface CampaignAmt { id: string; amount: number; }
    interface AggEntry { value: number; campaigns: CampaignAmt[]; rowKeys: string[]; }
    const aggregate = (pts: Pt[]): Map<string, AggEntry> => {
      const m = new Map<string, AggEntry>();
      pts.forEach(p => {
        const e = m.get(p.date) ?? { value: 0, campaigns: [], rowKeys: [] };
        e.value += p.value;
        const existing = e.campaigns.find(c => c.id === p.campaignId);
        if (existing) existing.amount += p.value;
        else e.campaigns.push({ id: p.campaignId, amount: p.value });
        if (!e.rowKeys.includes(p.rowKey)) e.rowKeys.push(p.rowKey);
        m.set(p.date, e);
      });
      return m;
    };

    const paidMap = aggregate(paidPts);
    const projMap  = aggregate(projPts);

    const toTs = (d: string) => new Date(d + 'T00:00:00').getTime();

    // Cumulative paid — one point per distinct paid date
    const sortedPaidDates = Array.from(paidMap.keys()).sort();
    let paidRunning = 0;
    const paidChartPts: { x: number; y: number; delta: number; campaigns: CampaignAmt[]; rowKeys: string[] }[] = [];
    for (const d of sortedPaidDates) {
      const e = paidMap.get(d)!;
      paidRunning += e.value;
      paidChartPts.push({ x: toTs(d), y: paidRunning, delta: e.value, campaigns: e.campaigns, rowKeys: e.rowKeys });
    }
    const lastPaidTotal = paidRunning;
    const lastPaidTs = paidChartPts.length > 0 ? paidChartPts[paidChartPts.length - 1].x : null;

    // Cumulative projected — bridge from lastPaidTs, then one point per distinct proj date
    const sortedProjDates = Array.from(projMap.keys()).sort();
    let projRunning = lastPaidTotal;
    const projChartPts: { x: number; y: number; delta: number; campaigns: CampaignAmt[]; rowKeys: string[] }[] = [];
    if (lastPaidTs != null) {
      projChartPts.push({ x: lastPaidTs, y: lastPaidTotal, delta: 0, campaigns: [], rowKeys: [] }); // bridge
    }
    for (const d of sortedProjDates) {
      const e = projMap.get(d)!;
      projRunning += e.value;
      projChartPts.push({ x: toTs(d), y: projRunning, delta: e.value, campaigns: e.campaigns, rowKeys: e.rowKeys });
    }

    // Salary — two points spanning the full date range
    const salary = this.selectedAgentSalary;
    const allTs = [...paidChartPts, ...projChartPts].map(p => p.x);
    const minTs = Math.min(...allTs);
    const maxTs = Math.max(...allTs);
    const salaryPts = salary != null
      ? [{ x: minTs, y: salary }, { x: maxTs, y: salary }]
      : [];

    // No bonus threshold lines for Agent/Casting (only Scouting has them, handled in buildScoutingChart)
    const bonusThresholdDatasets: any[] = [];

    this.chartData = {
      datasets: [
        {
          label: 'Commission Earned',
          data: paidChartPts as any,
          borderColor: '#4ade80',
          backgroundColor: 'rgba(74,222,128,0.15)',
          borderWidth: 2,
          pointRadius: 2,
          pointHoverRadius: 5,
          tension: 0,
          fill: false
        },
        {
          label: 'Projected Commission',
          data: projChartPts as any,
          borderColor: '#60a5fa',
          backgroundColor: 'rgba(96,165,250,0.15)',
          borderWidth: 2,
          borderDash: [6, 4],
          pointRadius: 2,
          pointHoverRadius: 5,
          tension: 0,
          fill: false
        },
        ...(salary != null ? [{
          label: 'Annual Salary',
          data: salaryPts as any,
          borderColor: '#f97316',
          backgroundColor: 'transparent',
          borderWidth: 2,
          borderDash: [8, 4],
          pointRadius: 0,
          pointHoverRadius: 0,
          tension: 0,
          fill: false
        }] : []),
        ...bonusThresholdDatasets
      ]
    };

    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: 'top',
          labels: {
            color: '#e8e8e8',
            usePointStyle: true,
            pointStyle: 'line',
            pointStyleWidth: 40,
            generateLabels: (chart) => {
              const defaults = Chart.defaults.plugins.legend.labels.generateLabels(chart);
              defaults.forEach((item, i) => {
                const ds = chart.data.datasets[i] as any;
                item.lineDash = ds?.borderDash ?? [];
                item.pointStyle = 'line';
              });
              return defaults.filter(item => item.text !== '');
            }
          }
        },
        tooltip: {
          enabled: false,
          external: this.externalTooltip
        }
      },
      scales: {
        x: {
          type: 'linear',
          ticks: {
            color: '#e8e8e8',
            maxRotation: 45,
            callback: (value) => {
              const d = new Date(value as number);
              return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
            }
          },
          afterBuildTicks: (axis: any) => {
            const min = axis.min;
            const max = axis.max;
            const ticks: { value: number }[] = [];
            const d = new Date(min);
            d.setDate(1);
            d.setHours(0, 0, 0, 0);
            if (d.getTime() < min) { d.setMonth(d.getMonth() + 1); }
            while (d.getTime() <= max) {
              ticks.push({ value: d.getTime() });
              d.setMonth(d.getMonth() + 1);
            }
            axis.ticks = ticks;
          },
          grid: { color: 'rgba(255,255,255,0.08)' }
        },
        y: {
          beginAtZero: true,
          ticks: {
            color: '#e8e8e8',
            callback: value => `$${(+value).toLocaleString('en-US')}`
          },
          grid: { color: 'rgba(255,255,255,0.08)' },
          title: { display: true, text: 'Commission ($)', color: '#e8e8e8' }
        }
      }
    };
  }

  // ─── Scouting chart: cumulative signing bonus by roster.signDate ──────────────

  private buildScoutingChart(_filteredCampaigns: Campaign[]): void {
    const signings = this.rosters
      .filter(r => r.source?.trim() === this.selectedSource && !!r.signDate && !isNaN(new Date(r.signDate).getTime()));

    if (signings.length === 0) { this.chartData = null; return; }

    interface SigningAmt { id: string; label: string; amount: number; }
    const aggMap = new Map<string, { value: number; campaigns: SigningAmt[] }>();
    signings.forEach(r => {
      const e = aggMap.get(r.signDate) ?? { value: 0, campaigns: [] };
      e.value += 400;
      e.campaigns.push({ id: r.id, label: r.name || '—', amount: 400 });
      aggMap.set(r.signDate, e);
    });

    const toTs = (d: string) => new Date(d + 'T00:00:00').getTime();
    const sortedDates = Array.from(aggMap.keys()).sort();
    let running = 0;
    const chartPts: { x: number; y: number; delta: number; campaigns: SigningAmt[]; rowKeys: string[] }[] = [];
    for (const d of sortedDates) {
      const e = aggMap.get(d)!;
      running += e.value;
      chartPts.push({ x: toTs(d), y: running, delta: e.value, campaigns: e.campaigns, rowKeys: [] });
    }

    this.chartPlugins = [];

    this.chartData = {
      datasets: [
        {
          label: 'Bonus Amount',
          data: chartPts as any,
          borderColor: '#4ade80',
          backgroundColor: 'rgba(74,222,128,0.15)',
          borderWidth: 2,
          pointRadius: 2,
          pointHoverRadius: 5,
          tension: 0,
          fill: false
        }
      ]
    };

    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: 'top',
          labels: {
            color: '#e8e8e8',
            usePointStyle: true,
            pointStyle: 'line',
            pointStyleWidth: 40,
            generateLabels: (chart) => {
              const defaults = Chart.defaults.plugins.legend.labels.generateLabels(chart);
              defaults.forEach((item, i) => {
                const ds = chart.data.datasets[i] as any;
                item.lineDash = ds?.borderDash ?? [];
                item.pointStyle = 'line';
              });
              return defaults.filter(item => item.text !== '');
            }
          }
        },
        tooltip: { enabled: false, external: this.externalTooltip }
      },
      scales: {
        x: {
          type: 'linear',
          ticks: {
            color: '#e8e8e8',
            maxRotation: 45,
            callback: (value) => {
              const d = new Date(value as number);
              return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
            }
          },
          afterBuildTicks: (axis: any) => {
            const min = axis.min; const max = axis.max;
            const ticks: { value: number }[] = [];
            const d = new Date(min); d.setDate(1); d.setHours(0, 0, 0, 0);
            if (d.getTime() < min) { d.setMonth(d.getMonth() + 1); }
            while (d.getTime() <= max) { ticks.push({ value: d.getTime() }); d.setMonth(d.getMonth() + 1); }
            axis.ticks = ticks;
          },
          grid: { color: 'rgba(255,255,255,0.08)' }
        },
        y: {
          beginAtZero: true,
          ticks: { color: '#e8e8e8', callback: value => `$${(+value).toLocaleString('en-US')}` },
          grid: { color: 'rgba(255,255,255,0.08)' },
          title: { display: true, text: 'Bonus Amount ($)', color: '#e8e8e8' }
        }
      }
    };
  }

  // ─── Bonus builders ────────────────────────────────────────────────────────

  private buildBonusData(): void {
    const agentUser = this.selectedAgentUser;
    if (!agentUser) { this.bonusAmount = 0; this.bonusRows = []; return; }

    const role = agentUser.role;

    // ── Scouting: entirely different model — no commission events ─────────────
    if (role === UserRole.Scouting) {
      const signingBonuses = this.buildScoutingSigningBonuses();
      this.bonusAmount = signingBonuses.reduce((s, b) => s + b.amount, 0);
      this.bonusRows = this.buildScoutingBonusTable();
      return;
    }

    if (role !== UserRole.Agent && role !== UserRole.Casting) {
      this.bonusAmount = 0;
      this.bonusRows = [];
      return;
    }

    // ── Agent / Casting: commission-event model ───────────────────────────────
    const salary = agentUser.salary ?? 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const events: CommEvent[] = [];
    for (const row of this.rows) {
      if (row.isPaid) {
        const date = row.paidDate || row.invoiceDueDate || row.campaignCreatedDate;
        events.push({ date, amount: row.commissionEarned, isProjected: false, rowKey: row.rowKey });
      } else if (row.isNoInvoice) {
        const projDate = this.projectedPayDate(row.campaignCreatedDate);
        events.push({ date: projDate, amount: row.campaignRate * row.campaignCommission / 100, isProjected: true, rowKey: row.rowKey });
      } else {
        const base = row.campaignCreatedDate ? new Date(row.campaignCreatedDate + 'T00:00:00') : today;
        const naiveDate = row.invoiceDueDate
          ? new Date(row.invoiceDueDate + 'T00:00:00')
          : new Date(base.getTime() + 71 * 24 * 60 * 60 * 1000);
        const commissionAmt = row.commissionAmount ?? 0;
        const amount = (row.invoiceAmount - (row.expenseAmount ?? 0) - commissionAmt) * row.campaignCommission / 100 + commissionAmt;
        if (naiveDate < today) {
          const [d1, d2] = this.twoMonthSpreadDates(row.invoiceDueDate || row.campaignCreatedDate);
          events.push({ date: d1, amount: amount / 2, isProjected: true, rowKey: row.rowKey });
          events.push({ date: d2, amount: amount / 2, isProjected: true, rowKey: row.rowKey });
        } else {
          events.push({ date: row.invoiceDueDate || row.campaignCreatedDate, amount, isProjected: true, rowKey: row.rowKey });
        }
      }
    }
    events.sort((a, b) => a.date.localeCompare(b.date));

    const realized = events.filter(e => !e.isProjected).reduce((s, e) => s + e.amount, 0);
    this.bonusAmount = Math.max(0, realized - salary) * 0.15;
    this.bonusRows = this.buildAgentBonusTable(events, salary);
  }

  private buildAgentBonusTable(events: CommEvent[], salary: number): BonusRow[] {
    if (events.length === 0) return [];

    const firstDate = new Date(events[0].date + 'T00:00:00');
    const lastDate  = new Date(events[events.length - 1].date + 'T00:00:00');

    // Generate 1st/15th payout dates spanning all events
    const payoutDates: Date[] = [];
    let cur = new Date(firstDate.getFullYear(), firstDate.getMonth(), 1);
    while (cur <= lastDate) {
      payoutDates.push(new Date(cur));
      cur = cur.getDate() === 1
        ? new Date(cur.getFullYear(), cur.getMonth(), 15)
        : new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
    }
    // One extra period to capture events up to lastDate
    const tail = payoutDates[payoutDates.length - 1];
    payoutDates.push(tail.getDate() === 1
      ? new Date(tail.getFullYear(), tail.getMonth(), 15)
      : new Date(tail.getFullYear(), tail.getMonth() + 1, 1));

    const toStr = (d: Date) => d.toISOString().substring(0, 10);
    const rows: BonusRow[] = [];
    let running = 0;

    for (let i = 0; i < payoutDates.length - 1; i++) {
      const startStr = toStr(payoutDates[i]);
      const endStr   = toStr(payoutDates[i + 1]);

      const periodEvents = events.filter(e => e.date >= startStr && e.date < endStr);
      if (periodEvents.length === 0) continue;

      const dateLabel = this.formatBonusDate(endStr);

      // Split events by realized vs projected and apply each portion against the
      // salary threshold separately so the schedule can show two rows per period.
      // Realized is applied first so projected only accrues bonus on commission
      // that exceeds salary after all realized commission is counted.
      const buckets: { events: CommEvent[]; isProjected: boolean }[] = [
        { events: periodEvents.filter(e => !e.isProjected), isProjected: false },
        { events: periodEvents.filter(e =>  e.isProjected), isProjected: true  },
      ];

      for (const bucket of buckets) {
        const bucketAmount = bucket.events.reduce((s, e) => s + e.amount, 0);
        if (bucketAmount === 0) continue;

        const prevRunning = running;
        running += bucketAmount;

        if (running <= salary) continue;

        const excessNew  = running - salary;
        const excessPrev = Math.max(0, prevRunning - salary);
        const periodCommission = excessNew - excessPrev;
        const bonus = periodCommission * 0.15;
        if (bonus <= 0) continue;

        rows.push({
          date: dateLabel,
          amount: bonus,
          isProjected: bucket.isProjected,
          rowKeys: [...new Set(bucket.events.map(e => e.rowKey))],
          periodCommission
        });
      }
    }
    return rows;
  }

  private buildScoutingSigningBonuses(): BonusRow[] {
    return this.rosters
      .filter(r => r.source?.trim() === this.selectedSource && !!r.signDate && !isNaN(new Date(r.signDate).getTime()))
      .sort((a, b) => a.signDate.localeCompare(b.signDate))
      .map(r => ({
        date: this.formatBonusDate(r.signDate),
        amount: 400,
        isProjected: false,
        rowKeys: [] as string[],
        periodCommission: 0,
        rosterName: r.name || '—'
      }));
  }

  private buildScoutingBonusTable(): BonusRow[] {
    // Only the $400 per signed roster bonus (already sorted by signDate)
    return this.buildScoutingSigningBonuses();
  }

  private formatBonusDate(dateStr: string): string {
    const d = new Date(dateStr + 'T00:00:00');
    const month = d.toLocaleDateString('en-US', { month: 'short' });
    const day = d.getDate();
    const suffix = (day === 1 || day === 21 || day === 31) ? 'st'
                 : (day === 2 || day === 22)               ? 'nd'
                 : (day === 3 || day === 23)               ? 'rd' : 'th';
    return `${month} ${day}${suffix}, ${d.getFullYear()}`;
  }
}
